import { Fragment, ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';
import { LatLngBounds, LatLngExpression } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import SectionHeader from '../../components/SectionHeader';
import Spinner from '../../components/Spinner';
import Button from '../../components/Button';
import { FitBounds, BoundsListener, MapAutoResize } from '../../components/MapBoundsComponents';
import { useGatesWMinRoute } from '../../hooks/useGates';
import { useGateSummaries, useGateFocus, useMapBounds } from '../../hooks/useGateMap';
import { createGateIcon } from '../../utils/gateMapUtils';
import markerIconPng from '../../icons/marker-icon-green.png';
import endMarkerIconPng from '../../icons/marker-icon-red.png';
import { GateSummary } from '../../types/GateSummary';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUpdateSession } from '../../hooks/useSessions';
import { useRoutePaths } from '../../hooks/useRoutePaths';
import RoutePolylines from '../../components/RoutePolylines';
import toast from 'react-hot-toast';
import { haversineDistanceMeters } from '../../utils/geoDistance';

type ProximityFilter = {
  lat: number;
  lon: number;
  radiusMeters: number;
};

type RouteSelectionState = {
  returnTo?: string;
  selectedGateId?: string | null;
  sessionId?: string;
  hasCallback?: boolean;
  hideCreateNew?: boolean;
  proximityFilter?: ProximityFilter | null;
};

export default function RouteSelection(): ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const routeState = location.state as RouteSelectionState | null;
  const returnTo = routeState?.returnTo;
  const selectedGateIdFromState = routeState?.selectedGateId ?? null;
  const sessionId = routeState?.sessionId; // Session ID if coming from session view

  const updateSession = useUpdateSession(sessionId ?? '');
  const { data: gates, isPending, isError } = useGatesWMinRoute();

  const allGateSummaries = useGateSummaries(gates);
  const proximityFilter = routeState?.proximityFilter;

  const gateSummaries = useMemo(() => {
    if (!proximityFilter) return allGateSummaries;
    return allGateSummaries.filter((gs) => {
      const [lat, lon] = gs.start as [number, number];
      const dist = haversineDistanceMeters(
        proximityFilter.lat, proximityFilter.lon, lat, lon,
      );
      return dist <= proximityFilter.radiusMeters;
    });
  }, [allGateSummaries, proximityFilter]);

  const {
    activeGateId,
    setActiveGateId,
    focusMode,
    setFocusMode,
    selectedGate,
    fitBoundsTrigger,
    handleFocusGate,
    handleZoomAll,
  } = useGateFocus(gateSummaries, {
    initialGateId: selectedGateIdFromState,
    initialFocusMode: selectedGateIdFromState ? 'selected' : 'all',
  });

  const { bounds, mapCenter, showBothGates, handleMapBoundsChange } = useMapBounds(
    gateSummaries,
    selectedGate,
    focusMode,
  );

  const [viewedGateIds, setViewedGateIds] = useState<string[]>([]);

  const viewedRouteIds = useMemo(() => {
    const routeIds = viewedGateIds
      .map((id) => gateSummaries.find((g) => g.id === id)?.gate.route_id)
      .filter(Boolean) as string[];
    return [...new Set(routeIds)];
  }, [viewedGateIds, gateSummaries]);

  const routePathsById = useRoutePaths(viewedRouteIds);

  // When the focused gate is being viewed, widen the fit to cover its route path too.
  const displayBounds = useMemo(() => {
    if (focusMode !== 'selected' || !selectedGate) return bounds;
    if (!viewedGateIds.includes(selectedGate.id)) return bounds;
    const points: LatLngExpression[] = [selectedGate.start];
    if (selectedGate.end) points.push(selectedGate.end);
    const path = routePathsById.get(selectedGate.gate.route_id);
    if (path) points.push(...path);
    return new LatLngBounds(points);
  }, [bounds, focusMode, selectedGate, viewedGateIds, routePathsById]);

  const handleToggleView = useCallback(
    (marker: GateSummary) => {
      const turningOn = !viewedGateIds.includes(marker.id);
      setViewedGateIds((prev) =>
        turningOn ? [...prev, marker.id] : prev.filter((id) => id !== marker.id),
      );
      if (turningOn) handleFocusGate(marker.id);
    },
    [viewedGateIds, handleFocusGate],
  );

  useEffect(() => {
    if (!selectedGateIdFromState) return;
    setActiveGateId((prev) => (prev === selectedGateIdFromState ? prev : selectedGateIdFromState));
    setFocusMode('selected');
  }, [selectedGateIdFromState, setActiveGateId, setFocusMode]);

  const getGateIcon = useCallback(
    (type: 'start' | 'end', label: string) => createGateIcon(type, label, markerIconPng, endMarkerIconPng),
    [],
  );

  const handleSelectGate = useCallback(
    async (summary: GateSummary) => {
      handleFocusGate(summary.id);

      // Directly assign gate to session
      if (sessionId) {
        try {
          await updateSession.mutateAsync({ gates_id: summary.gate.id });
          toast.success('Gate assigned to session');
        } catch (error) {
          console.error('Error assigning gate to session:', error);
          toast.error('Failed to assign gate');
        }
      }

      // Also fire legacy callback if available
      if (routeState?.hasCallback && (window as any).__gateSelectCallback) {
        (window as any).__gateSelectCallback(summary.gate.id);
      }

      if (returnTo) {
        navigate(returnTo);
      }
    },
    [handleFocusGate, updateSession, sessionId, navigate, returnTo, routeState?.hasCallback],
  );

  const handleCreateNew = useCallback(() => {
    // Find current gate lat/lon from the selected gate (if any)
    const currentGate = selectedGateIdFromState
      ? gateSummaries.find((g) => g.id === selectedGateIdFromState)?.gate
      : undefined;

    navigate('/gate-from-run', {
      state: {
        returnTo,
        sessionId,
        existingGatesLat: currentGate?.gates_lat,
        existingGatesLon: currentGate?.gates_lon,
        hasCallback: routeState?.hasCallback,
      },
    });
  }, [navigate, returnTo, sessionId, selectedGateIdFromState, gateSummaries, routeState?.hasCallback]);

  const backLink = returnTo ? { to: returnTo, text: sessionId ? 'Back to Session' : 'Back to Calibration' } : undefined;

  const headerActions = sessionId && !routeState?.hideCreateNew ? (
    <button
      type="button"
      onClick={handleCreateNew}
      className="cr-rounded-md cr-bg-blue-500 cr-px-3 cr-py-1.5 cr-text-sm cr-font-semibold cr-font-main cr-text-white cr-transition-colors hover:cr-bg-blue-800"
    >
      Create New
    </button>
  ) : undefined;

  return (
    <div className="cr-w-full">
      <SectionHeader
        title={proximityFilter ? 'Nearby Gates' : 'Route Selection'}
        isLoading={isPending}
        backLink={backLink}
        actions={headerActions}
      />

      <div className="cr-mt-6 cr-grid cr-gap-6">
        <div className="cr-relative cr-h-[500px] cr-overflow-hidden cr-rounded-xl cr-bg-white cr-shadow-sm">
          {isPending && (
            <div className="cr-flex cr-h-full cr-items-center cr-justify-center">
              <Spinner isFetching={true} />
            </div>
          )}

          {isError && !isPending && (
            <div className="cr-flex cr-h-full cr-items-center cr-justify-center cr-text-red-600">
              Unable to load gates for this team. Please try again later.
            </div>
          )}

          {!isError && !isPending && gateSummaries.length === 0 && (
            <div className="cr-flex cr-h-full cr-items-center cr-justify-center">
              No gates available for this team yet.
            </div>
          )}

          {!isError && !isPending && gateSummaries.length > 0 && (
            <>
              {selectedGate && gateSummaries.length > 1 && (
                <div className="cr-absolute cr-right-4 cr-top-4 cr-z-[1000]">
                  <button
                    type="button"
                    onClick={handleZoomAll}
                    className="cr-rounded-md cr-bg-white cr-px-3 cr-py-1 cr-text-xs cr-font-semibold cr-text-blue-900 cr-shadow-sm hover:cr-bg-cirrusBlue-25"
                  >
                    All Gates
                  </button>
                </div>
              )}
              <MapContainer center={mapCenter} zoom={13} scrollWheelZoom className="cr-h-full cr-w-full">
                <MapAutoResize />
                <FitBounds bounds={displayBounds} trigger={fitBoundsTrigger} />
                <BoundsListener onBoundsChange={handleMapBoundsChange} />
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <RoutePolylines routeIds={viewedRouteIds} pathsById={routePathsById} />
                {gateSummaries.map((marker) => (
                  <Fragment key={marker.id}>
                    <Marker position={marker.start} icon={getGateIcon('start', String(marker.order))}>
                      <Popup>
                        <div className="cr-min-w-[180px]">
                          <p className="cr-font-semibold">{marker.popupLabel}</p>
                          <p className="cr-text-xs cr-text-gray-500">Gate #{marker.order}</p>
                          <p className="cr-text-xs cr-text-gray-500">Start gate</p>
                          <p className="cr-text-sm cr-text-gray-600">
                            {marker.isValid ? 'Valid' : 'Not yet validated'}
                          </p>
                        </div>
                      </Popup>
                    </Marker>
                    {(showBothGates || viewedGateIds.includes(marker.id)) && marker.end && (
                      <Marker position={marker.end} icon={getGateIcon('end', String(marker.order))}>
                        <Popup>
                          <div className="cr-min-w-[180px]">
                            <p className="cr-font-semibold">{marker.popupLabel}</p>
                            <p className="cr-text-xs cr-text-gray-500">Gate #{marker.order}</p>
                            <p className="cr-text-xs cr-text-gray-500">End gate</p>
                            <p className="cr-text-sm cr-text-gray-600">
                              {marker.isValid ? 'Valid' : 'Not yet validated'}
                            </p>
                          </div>
                        </Popup>
                      </Marker>
                    )}
                  </Fragment>
                ))}
              </MapContainer>
            </>
          )}
        </div>

        <div className="cr-rounded-xl cr-bg-white cr-p-6 cr-shadow-sm">
          <h2 className="cr-mb-2 cr-text-lg cr-font-semibold">Gate Pairs</h2>
          <p className="cr-mb-4 cr-text-sm cr-text-gray-600">
            Gate pairs are numbered for quick reference. Use View to show a route and both its gates on the map
            (several can be viewed at once) or Select to apply the gates.
          </p>

          <div className="cr-flex cr-flex-col cr-gap-3">
            {gateSummaries.map((marker) => {
              const isActive = marker.id === activeGateId;
              return (
                <div
                  key={marker.id}
                  onClick={() => handleFocusGate(marker.id)}
                  className={`cr-flex cr-cursor-pointer cr-items-center cr-justify-between
                  cr-rounded-lg cr-border cr-p-4 cr-transition-colors ${
                    isActive ? 'cr-border-cirrusBlue-200 cr-bg-cirrusBlue-25' : 'cr-border-gray-200'
                  }`}
                >
                  <div className="cr-flex cr-items-start cr-gap-3">
                    <span className="cr-flex cr-h-6 cr-w-6 cr-items-center cr-justify-center cr-rounded-full cr-bg-cirrusBlue-100 cr-text-xs cr-font-semibold cr-text-white">
                      {marker.order}
                    </span>
                    <div>
                      <p className="cr-font-medium">{marker.label}</p>
                      {marker.routeDescription && (
                        <p className="cr-text-xs cr-text-gray-500">{marker.routeDescription}</p>
                      )}
                      <p className="cr-text-xs cr-text-gray-500">ID: {marker.id}</p>
                    </div>
                  </div>
                  <div className="cr-flex cr-items-center cr-gap-2">
                    <Button
                      variant={viewedGateIds.includes(marker.id) ? 'secondary' : 'outline'}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleToggleView(marker);
                      }}
                    >
                      View
                    </Button>
                    <Button
                      onClick={(event) => {
                        event.stopPropagation();
                        handleSelectGate(marker);
                      }}
                    >
                      Select
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
